import {
  AdditiveBlending,
  BufferAttribute,
  BufferGeometry,
  Color,
  Points,
  type Scene,
  ShaderMaterial,
  type Vector3,
} from 'three';

export interface EmitOptions {
  color: Color | string;
  count: number;
  /** Prędkość początkowa (jednostki/s). */
  speed: number;
  /** Czas życia (s). */
  life: number;
  /** Dodatkowa prędkość w górę. */
  up?: number;
  gravity?: number;
  /** Rozrzut pozycji startowej. */
  spread?: number;
  /** Rozmiar w jednostkach świata (domyślnie rozmiar systemu). */
  size?: number;
  /** Mnożnik rozmiaru na końcu życia (np. 2.5 = dym rośnie, 0.2 = iskra gaśnie). */
  grow?: number;
  /** Start w kolorze białego żaru, potem przejście w `color`. */
  hot?: boolean;
  /** Opór powietrza (0 = brak). */
  drag?: number;
}

const vertexShader = /* glsl */ `
  attribute float aSize;
  attribute vec3 aColor;
  attribute float aAlpha;
  uniform float uScale;
  varying vec3 vColor;
  varying float vAlpha;
  void main() {
    vColor = aColor;
    vAlpha = aAlpha;
    vec4 mv = modelViewMatrix * vec4(position, 1.0);
    gl_PointSize = aSize * uScale / -mv.z;
    gl_Position = projectionMatrix * mv;
  }`;

/** Miękki krąg z jasnym rdzeniem — żadnych kwadratów. */
const fragmentShader = /* glsl */ `
  varying vec3 vColor;
  varying float vAlpha;
  void main() {
    float d = length(gl_PointCoord - 0.5);
    if (d > 0.5) discard;
    float halo = 1.0 - smoothstep(0.0, 0.5, d);
    float core = 1.0 - smoothstep(0.0, 0.22, d);
    gl_FragColor = vec4(vColor * (halo * halo + core * 1.4), halo * vAlpha);
  }`;

/**
 * System cząsteczek na jednym obiekcie Points (jedno wywołanie rysowania), z blendingiem addytywnym.
 * Każda cząsteczka ma własny rozmiar, kolor zmieniający się w czasie i zanikanie.
 */
export class Particles {
  private readonly pos: Float32Array;
  private readonly vel: Float32Array;
  private readonly col: Float32Array;
  private readonly from: Float32Array;
  private readonly to: Float32Array;
  private readonly size: Float32Array;
  private readonly size0: Float32Array;
  private readonly grow: Float32Array;
  private readonly alpha: Float32Array;
  private readonly life: Float32Array;
  private readonly maxLife: Float32Array;
  private readonly gravity: Float32Array;
  private readonly drag: Float32Array;
  private readonly geometry = new BufferGeometry();
  private readonly material: ShaderMaterial;
  private next = 0;

  constructor(
    scene: Scene,
    private readonly capacity: number,
    private readonly baseSize: number,
  ) {
    const f = (n: number) => new Float32Array(capacity * n);
    this.pos = f(3).fill(-9999);
    this.vel = f(3);
    this.col = f(3);
    this.from = f(3);
    this.to = f(3);
    this.size = f(1);
    this.size0 = f(1);
    this.grow = f(1).fill(1);
    this.alpha = f(1);
    this.life = f(1);
    this.maxLife = f(1).fill(1);
    this.gravity = f(1);
    this.drag = f(1);
    this.geometry.setAttribute('position', new BufferAttribute(this.pos, 3));
    this.geometry.setAttribute('aColor', new BufferAttribute(this.col, 3));
    this.geometry.setAttribute('aSize', new BufferAttribute(this.size, 1));
    this.geometry.setAttribute('aAlpha', new BufferAttribute(this.alpha, 1));
    this.material = new ShaderMaterial({
      uniforms: { uScale: { value: 600 } },
      vertexShader,
      fragmentShader,
      transparent: true,
      blending: AdditiveBlending,
      depthWrite: false,
    });
    const points = new Points(this.geometry, this.material);
    points.frustumCulled = false;
    points.renderOrder = 3;
    scene.add(points);
  }

  /** Przelicznik rozmiaru świata na piksele — wywołaj przy zmianie rozmiaru okna. */
  setViewport(heightPx: number, fovDeg: number): void {
    this.material.uniforms.uScale!.value = heightPx / (2 * Math.tan((fovDeg * Math.PI) / 360));
  }

  emit(at: Vector3, o: EmitOptions): void {
    const c = new Color(o.color);
    for (let n = 0; n < o.count; n++) {
      const i = this.next;
      this.next = (this.next + 1) % this.capacity;
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(Math.random() * 2 - 1);
      const sp = o.speed * (0.4 + Math.random() * 0.6);
      const spread = o.spread ?? 0;
      const i3 = i * 3;
      this.pos[i3] = at.x + (Math.random() - 0.5) * spread;
      this.pos[i3 + 1] = at.y + (Math.random() - 0.5) * spread;
      this.pos[i3 + 2] = at.z + (Math.random() - 0.5) * spread;
      this.vel[i3] = Math.sin(phi) * Math.cos(theta) * sp;
      this.vel[i3 + 1] = Math.abs(Math.cos(phi)) * sp * 0.6 + (o.up ?? 0);
      this.vel[i3 + 2] = Math.sin(phi) * Math.sin(theta) * sp;
      // Żar: start jaśniejszy (bliżej bieli), koniec w kolorze docelowym.
      const h = o.hot ? 0.75 : 0;
      this.from[i3] = c.r + (1 - c.r) * h;
      this.from[i3 + 1] = c.g + (1 - c.g) * h;
      this.from[i3 + 2] = c.b + (1 - c.b) * h;
      this.to[i3] = c.r;
      this.to[i3 + 1] = c.g;
      this.to[i3 + 2] = c.b;
      this.size0[i] = (o.size ?? this.baseSize) * (0.7 + Math.random() * 0.6);
      this.grow[i] = o.grow ?? 1;
      this.life[i] = this.maxLife[i] = o.life * (0.6 + Math.random() * 0.4);
      this.gravity[i] = o.gravity ?? 0;
      this.drag[i] = o.drag ?? 0;
    }
  }

  update(dt: number): void {
    for (let i = 0; i < this.capacity; i++) {
      if (this.life[i]! <= 0) continue;
      this.life[i]! -= dt;
      const k = Math.max(0, this.life[i]! / this.maxLife[i]!); // 1 → 0
      const t = 1 - k;
      const i3 = i * 3;
      const damp = Math.max(0, 1 - this.drag[i]! * dt);
      this.vel[i3 + 1]! -= this.gravity[i]! * dt;
      for (let a = 0; a < 3; a++) {
        this.vel[i3 + a]! *= damp;
        this.pos[i3 + a]! += this.vel[i3 + a]! * dt;
        this.col[i3 + a] = this.from[i3 + a]! + (this.to[i3 + a]! - this.from[i3 + a]!) * Math.min(1, t * 2.5);
      }
      this.size[i] = this.size0[i]! * (1 + (this.grow[i]! - 1) * t);
      // Szybkie pojawienie się, łagodne zgaśnięcie.
      this.alpha[i] = Math.min(1, t * 12) * k * k * (3 - 2 * k);
      if (k <= 0) {
        this.pos[i3 + 1] = -9999;
        this.alpha[i] = 0;
      }
    }
    for (const name of ['position', 'aColor', 'aSize', 'aAlpha']) this.geometry.attributes[name]!.needsUpdate = true;
  }
}
